import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Terms() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#F7F5F2] px-4 py-8 sm:py-14">
      <div className="max-w-3xl mx-auto">
        <Link
          to={user ? "/" : "/"}
          data-testid="terms-back"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B2A4A]/70 hover:text-[#E8621A] transition-colors duration-200 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="rounded-3xl bg-white border border-[#1B2A4A]/8 p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#1B2A4A]/10 text-[#1B2A4A] px-3 py-1 text-xs font-semibold tracking-widest uppercase">
            <FileText className="w-3 h-3" /> Terms of Service
          </div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-tight">
            Surviving Together: <span className="text-[#E8621A]">Thriving Together</span>
          </h1>
          <p className="mt-3 text-sm text-[#1B2A4A]/60">Last updated: October 10, 2026</p>

          <div className="mt-8 space-y-5 text-[#1B2A4A]/85 leading-relaxed">
            <p>
              By creating an account, you confirm you are 18 or older and agree to these terms and our{" "}
              <Link to="/guidelines" className="text-[#E8621A] font-semibold hover:underline">
                Community Guidelines
              </Link>
              .
            </p>

            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <span className="font-semibold text-[#1B2A4A]">Your account.</span> Keep your password private. You're responsible
                for activity on your account.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Community rules.</span> Be kind and honest. No spam, advertising,
                harassment, hate, or unsafe advice. We may remove content and warn, suspend or remove accounts, including the
                three-strikes system in the Guidelines.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Age.</span> You must be 18 or older. We may remove any account
                found to belong to someone under 18.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Your content.</span> You keep ownership of what you post. You give
                us permission to display it in the app and, if you opted in, in our newsletter.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Memberships and refunds.</span> Free is $0. Community Member is $2
                per month or $20 per year, billed through Stripe. Memberships renew automatically each month or year until
                cancelled. You can cancel at any time from your account, and you'll keep access until the end of the period
                you've paid for. Payments are non-refundable, except where required by law or if we made a billing error. If
                something went wrong, contact{" "}
                <a href="mailto:info@survivingtogether.org" className="text-[#E8621A] font-semibold hover:underline">
                  info@survivingtogether.org
                </a>{" "}
                within 14 days and we'll do our best to make it right.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Not professional advice.</span> Tips are shared by neighbors, not
                experts. Nothing here is financial, legal, medical or mental health advice. The Relaxation Room is for general
                calm and is not a treatment. If you're in crisis, call or text 988 (U.S.).
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">No guarantees.</span> The service is provided "as is." We can't
                promise it will always be available or error-free.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Limit of liability.</span> To the extent allowed by law, we aren't
                liable for indirect or consequential damages, and our total liability is limited to what you paid us in the past
                12 months.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Changes and ending.</span> We may update these terms or the
                service. You can delete your account at any time.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Law.</span> These terms follow Ohio law.
              </li>
              <li>
                <span className="font-semibold text-[#1B2A4A]">Contact.</span>{" "}
                <a href="mailto:info@survivingtogether.org" className="text-[#E8621A] font-semibold hover:underline">
                  info@survivingtogether.org
                </a>
              </li>
            </ol>
          </div>

          <div className="mt-10 pt-6 border-t border-[#1B2A4A]/10 flex flex-wrap gap-4 text-sm">
            <Link to="/privacy" data-testid="terms-link-privacy" className="text-[#E8621A] font-semibold hover:underline">
              Privacy Policy
            </Link>
            <Link to="/guidelines" data-testid="terms-link-guidelines" className="text-[#E8621A] font-semibold hover:underline">
              Community Guidelines
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
